// Canonical project interfaces. See spec/tech-arch.md §11.

import type { z } from 'zod'

/**
 * Trusted HTML markup — already escaped or sanitized.
 *
 * SECURITY: Constructed exclusively by `html` (src/render/html.ts) and
 * `sanitizeRichText` (src/render/sanitize.ts). These are THE trust
 * boundaries where user input is escape-on-interpolation or
 * DOMPurify-sanitized. Constructing `new HtmlString(rawInput)` anywhere
 * else bypasses XSS prevention and is a security bug — flagged at
 * `/security-review` per spec/operating-contract.md §3.5 + §7.1.
 *
 * Runtime brand is the class itself (instanceof-checked); see D-021 in
 * spec/decision-log.md for the chosen pattern over a type-erased
 * `string & { __brand }`.
 */
export class HtmlString {
  constructor(public readonly value: string) {}
}

/**
 * Shared empty HtmlString sentinel for skip / auto-hide paths in the render
 * pipeline. Empty string carries no XSS surface, so this is one of the
 * documented exceptions to the "construct HtmlString only inside html.ts /
 * sanitize.ts" rule (D-021). Hoisted at the third use (P-019) — prior
 * file-local consumers were section-heading.ts (D-052) and render-block.ts
 * (D-077). render-email.ts is the third (empty-Email + all-skipped edge
 * cases). See D-080.
 */
export const EMPTY_HTML = new HtmlString('')

export type BlockCategory = 'header' | 'content' | 'footer' | 'meta'

/**
 * Preview-viewport options. Drives the iframe's outer pixel width:
 * 680px (matches `tokens.layout.containerWidth` per design-system.md) for desktop, 375px
 * (below the 480px mobile-breakpoint media query) for mobile. Hoisted
 * here at P-030 — six+ inline uses across render + preview layers.
 */
export type Viewport = 'desktop' | 'mobile'

/**
 * Per-block context computed by the email orchestrator at render time.
 * `followingBlocksAllHidden` powers `section-heading` auto-hide
 * (spec/tech-arch.md §11.5). Consumed by P-018+ render-block / render-email.
 *
 * **P-037.18 D-118 — `linkColor`.** Template-level CTA / link color derived
 * from the Email's first included Header block style. `renderEmail` looks
 * up `STYLE_LINK_COLOR[header.options.style]` and threads it via this
 * context. Default = `tokens.color['link-default']` (#c69b34) when no
 * Header is present or all Header blocks are excluded. Blocks read
 * `ctx.linkColor` and forward it to Components (SectionHeader,
 * SubSectionHeader, EventRow, WhatsAppRow) as a prop — Components can't
 * see RenderContext directly per the BlockDefinition contract.
 *
 * **P-037.19 D-125 — `subheaderImageUrl`.** Template-level subheader banner
 * URL derived from the same first-Header style. `renderEmail` looks up
 * `STYLE_SUBHEADER[header.options.style]`. Default = `STYLE_SUBHEADER.signal`
 * when no Header is present (Banks: "Signal by default"). Only the
 * `section-heading` Block forwards this to SectionHeader; other consumers
 * leave the prop unset and render without a banner.
 *
 * **P-037.23 D-131 — `tintColor`.** Template-level 10% tint of the per-style
 * link color, derived the same way (first-Header style → `STYLE_TINT_10`).
 * Default = `TINT_DEFAULT` (#f9f5eb — 10% of `link-default` over white)
 * when no Header is present. Used by the footer's outer-table bg
 * (P-037.23 — quiet per-Email-family tint replaces the prior generic
 * #e5e5e5 rule-subtle). Pre-computed hex literals because `opacity` /
 * `rgba()` are unreliable in email clients (Outlook desktop drops them).
 *
 * **P-037.28 D-136 — `ctaBgColor`.** Template-level CTA button background,
 * derived the same way (first-Header style → `STYLE_CTA_BG`). Default =
 * `CTA_BG_DEFAULT` (#162443 navy — matches the pre-D-136 text-block
 * button bg) when no Header is present. Used by the new `button` Block
 * (item 13 revised) so freeform CTAs inherit the per-Email family.
 *
 * **P-037.31 fourth post-Phase-Close §D — `precedingBlockId`.** Id of
 * the immediately-preceding RENDERED Block (skipped Blocks excluded);
 * `undefined` for the first rendered Block in an Email. Powers the
 * `big3-signature-events → sub-section-heading` auto-collapse seam:
 * the standalone `sub-section-heading` Block reads
 * `ctx.precedingBlockId === 'big3-signature-events'` and threads
 * `hideTopRule: true` (P-037.30 ninth post-Phase-Close §C — was
 * `noTopRule: true` pre-split; now `hideTopRule` drops just the rule
 * and `PAD_TOP` stays so the heading text has a 30 px gap below the
 * big-3's tinted footer edge). Populated by `renderEmail` during
 * the forward render pass — the inter-Block spacer pass already walks
 * the rendered-Block list in order; this field is the same "previous
 * rendered Block" carried into each Block's own render call. Optional
 * because the first rendered Block has no preceding one.
 */
export interface RenderContext {
  index: number
  totalBlocks: number
  isLast: boolean
  followingBlocksAllHidden: boolean
  linkColor: string
  subheaderImageUrl: string
  tintColor: string
  ctaBgColor: string
  precedingBlockId?: string
}

/**
 * The single contract every Block file exports. The form generator reads
 * `optionsSchema` / `inputsSchema`; the renderer reads `render`; auto-hide
 * logic reads `autoHide`. Per spec/tech-arch.md §11.4.
 *
 * Each Block file uses `defineBlock(...)` (src/render/define-block.ts) so
 * the Options / Inputs generics infer from the schemas without manual params.
 */
export interface BlockDefinition<Options extends z.ZodTypeAny, Inputs extends z.ZodTypeAny> {
  id: string
  label: string
  category: BlockCategory
  optionsSchema: Options
  inputsSchema: Inputs
  defaultOptions: z.infer<Options>
  defaultInputs: z.infer<Inputs>
  autoHide?: (data: { inputs: z.infer<Inputs> }) => boolean
  render: (
    data: { options: z.infer<Options>; inputs: z.infer<Inputs> },
    ctx: RenderContext,
  ) => HtmlString
}
